// ========================================
// 🔍 FUNÇÃO PARA EXTRAIR ACHADOS DA RESPOSTA IA
// ========================================
// Adicionar em: src/modules/ia-analise/utils/extrairAchadosIA.ts
// Ou diretamente no ListaExamesImagem.tsx

// ✅ Bloco 1 AI_34: campos conclusaoLaudistaLiteral, tipoLaudista, conclusaoLiteralGeradaEm, conclusaoLiteralStatus adicionados ao final
import type { AdequacaoAmostral, AchadosExplanteExtras } from "../../../types";

// ✅ B28.2pre (AI_50) — herda sub-interface AchadosExplanteExtras via composition.
// Rota C: mantém AchadosExame como interface principal e adiciona campos específicos
// de explante hepático via extends, sem poluir a definição base. Dívida B9
// (unificação de schemas duplicados) remanesce separada.
export interface AchadosExame extends AchadosExplanteExtras {
  tipo: string;
  data: string;
  lesoesHepaticas: string;
  statusMilao: "DENTRO" | "FORA" | "NÃO APLICÁVEL" | "";
  trombosePortal: string;
  tipoTrombose: "BLAND" | "TUMORAL" | "NÃO APLICÁVEL" | "";
  shunts: string;
  ascite: "SIM" | "NÃO" | "";
  esplenomegalia: string;
  outrosAchados: string;
  conclusao: string;
  conclusaoLaudistaLiteral?: string;
  tipoLaudista?:
    | "patologista"
    | "radiologista"
    | "endoscopista"
    | "ecografista"
    | "cardiologista"
    | "outro";
  conclusaoLiteralGeradaEm?: string;
  conclusaoLiteralStatus?: "sucesso" | "sem_secao_conclusao" | "erro_extracao";

  // ✅ AI_40 Patch 2 Alt 4.2 — adequação amostral (só biópsias)
  adequacaoAmostral?: AdequacaoAmostral;

  // ✅ B301 (AI_137) — campos ESTRUTURADOS de ENDOSCOPIA (EDA/colonoscopia).
  // O template getTemplateEndoscopia emite estes campos no bloco ACHADOS_EXAME;
  // antes eram descartados (parser só lia o schema de imagem). Opcionais →
  // imagem/biópsia/explante seguem inalterados.
  varizesEsofagicas?: string;
  varizesGastricas?: string;
  gastropatiaHipertensiva?: string;
  sinaisRisco?: string;
  gave?: string;
  classificacaoRisco?: string;

  // ✅ B301 cont. (AI_138) — campos ESTRUTURADOS de ELASTOGRAFIA (FibroScan /
  // elastografia hepática). O getTemplateElastografia já os emite; antes eram
  // descartados. Consumidos pelo insight de CSPH (ProntuarioAlertasIA) para ler
  // a rigidez de forma precisa (antes só regex do laudo). Opcionais → demais
  // tipos inalterados.
  rigidezKpa?: string;
  fibroseMetavir?: string;
  capEsteatose?: string;
}
// ═══════════════════════════════════════════════════════════════════
// 🔬 B28.2a (AI_51) — DETECÇÃO DE TIPO DE MATERIAL + EXTRATOR EXPLANTE
// ═══════════════════════════════════════════════════════════════════
// Classifica o material do exame a partir do campo TIPO e, quando for
// explante hepático, popula os 12 campos de AchadosExplanteExtras
// (introduzidos em AI_50 B28.2pre). Para imagem/biópsia/endoscopia/
// outros mantém o contrato base atual — regressão zero.
//
// Contrato de chaves do bloco EXPLANTE (a ser emitido pelo
// getTemplateBiopsia em B28.2b):
//   FOCALIDADE_TUMORAL | TAMANHO_MACRO_CM | TAMANHO_VIAVEL_CM
//   EFEITO_TERAPEUTICO_PCT_NECROSE | SATELITOSE
//   INVASAO_VASCULAR_MACRO | EXTENSAO_ESTRUTURAS_ADJACENTES
//   LINFONODOS_HILARES | PTNM_COMPLETO | TUMOR_SINCRONICO
//   MILAO_EXPLANTE_VIAVEL | HEPATOPATIA_SUBJACENTE
// ═══════════════════════════════════════════════════════════════════

export type TipoMaterialExame =
  | "EXPLANTE"
  | "BIOPSIA"
  | "IMAGEM"
  | "ENDOSCOPIA"
  | "ELASTOGRAFIA"
  | "OUTRO";

/**
 * Classifica o material do exame pelo campo TIPO.
 * Precedência: EXPLANTE > BIOPSIA > IMAGEM > ENDOSCOPIA > OUTRO.
 * "Biópsia do explante" é classificada como EXPLANTE (precedência).
 */
export function detectarTipoMaterial(tipo: string): TipoMaterialExame {
  if (!tipo) return "OUTRO";
  const t = tipo.toLowerCase();
  if (/\bexplante\b/.test(t)) return "EXPLANTE";
  if (/\bbi[óo]psia\b/.test(t)) return "BIOPSIA";
  // ELASTOGRAFIA antes de IMAGEM: uma "elastografia por RM" é elastografia,
  // não imagem seccional (B301 cont., AI_138).
  if (/\b(elastografia|fibroscan|elastograma)\b/.test(t)) return "ELASTOGRAFIA";
  if (
    /\b(tc|rm|rmn|usg|tomografia|ressonancia|ressonância|ultrassom|ultrasom|ultra-?som|pet(?:-?ct)?|eco(?:cardiograma|grafia)?|radiografia|rx|cintilografia)\b/.test(
      t
    )
  ) {
    return "IMAGEM";
  }
  if (
    /\b(endoscopia|eda|colonoscopia|cpre|colangiorm|colangio-?rm)\b/.test(t)
  ) {
    return "ENDOSCOPIA";
  }
  return "OUTRO";
}

/**
 * Extrai os 12 campos específicos de explante hepático.
 * Chamada apenas quando detectarTipoMaterial === "EXPLANTE".
 * Enquanto B28.2b não ativar as chaves no template, todas as
 * extrações retornam string vazia e o comportamento efetivo é nulo.
 */
function extrairCamposExplante(linhas: string[]): AchadosExplanteExtras {
  const extrairValor = (chave: string): string => {
    const linha = linhas.find((l) =>
      l.toUpperCase().startsWith(chave.toUpperCase() + ":")
    );
    if (!linha) return "";
    return linha.substring(linha.indexOf(":") + 1).trim();
  };

  return {
    focalidadeTumoral: extrairValor(
      "FOCALIDADE_TUMORAL"
    ) as AchadosExplanteExtras["focalidadeTumoral"],
    tamanhoMacroscopicoCm: extrairValor("TAMANHO_MACRO_CM"),
    tamanhoViavelCm: extrairValor("TAMANHO_VIAVEL_CM"),
    efeitoTerapeuticoPctNecrose: extrairValor("EFEITO_TERAPEUTICO_PCT_NECROSE"),
    satelitose: extrairValor(
      "SATELITOSE"
    ) as AchadosExplanteExtras["satelitose"],
    invasaoVascularMacro: extrairValor(
      "INVASAO_VASCULAR_MACRO"
    ) as AchadosExplanteExtras["invasaoVascularMacro"],
    extensaoEstruturasAdjacentes: extrairValor(
      "EXTENSAO_ESTRUTURAS_ADJACENTES"
    ),
    linfonodosHilares: extrairValor("LINFONODOS_HILARES"),
    ptnmCompleto: extrairValor("PTNM_COMPLETO"),
    tumorSincronico: extrairValor("TUMOR_SINCRONICO"),
    milaoExplanteViavel: extrairValor(
      "MILAO_EXPLANTE_VIAVEL"
    ) as AchadosExplanteExtras["milaoExplanteViavel"],
    hepatopatiaSubjacente: extrairValor("HEPATOPATIA_SUBJACENTE"),
  };
}

/**
 * ✅ B301 (AI_137) — extrai os campos específicos de ENDOSCOPIA (EDA/colonoscopia).
 * Chamado quando detectarTipoMaterial === "ENDOSCOPIA". As chaves são as emitidas
 * pelo getTemplateEndoscopia no bloco ACHADOS_EXAME. Espelha extrairCamposExplante.
 */
function extrairCamposEndoscopia(linhas: string[]): Partial<AchadosExame> {
  const extrairValor = (chave: string): string => {
    const linha = linhas.find((l) =>
      l.toUpperCase().startsWith(chave.toUpperCase() + ":")
    );
    if (!linha) return "";
    return linha.substring(linha.indexOf(":") + 1).trim();
  };

  return {
    varizesEsofagicas: extrairValor("VARIZES_ESOFAGICAS"),
    varizesGastricas: extrairValor("VARIZES_GASTRICAS"),
    gastropatiaHipertensiva: extrairValor("GASTROPATIA_HIPERTENSIVA"),
    sinaisRisco: extrairValor("SINAIS_RISCO"),
    gave: extrairValor("GAVE"),
    classificacaoRisco: extrairValor("CLASSIFICACAO_RISCO"),
  };
}

/**
 * ✅ B301 cont. (AI_138) — extrai os campos específicos de ELASTOGRAFIA.
 * Chamado quando detectarTipoMaterial === "ELASTOGRAFIA". As chaves são as
 * emitidas pelo getTemplateElastografia no bloco ACHADOS_EXAME. Espelha
 * extrairCamposEndoscopia.
 */
function extrairCamposElastografia(linhas: string[]): Partial<AchadosExame> {
  const extrairValor = (chave: string): string => {
    const linha = linhas.find((l) =>
      l.toUpperCase().startsWith(chave.toUpperCase() + ":")
    );
    if (!linha) return "";
    return linha.substring(linha.indexOf(":") + 1).trim();
  };

  return {
    rigidezKpa: extrairValor("RIGIDEZ"),
    fibroseMetavir: extrairValor("FIBROSE_METAVIR"),
    capEsteatose: extrairValor("CAP"),
  };
}

/**
 * Extrai os achados estruturados da resposta da IA
 * Procura pelo bloco ```ACHADOS_EXAME ... ``` no texto
 */
export function extrairAchadosIA(respostaIA: string): AchadosExame | null {
  try {
    // Procurar o bloco de achados
    // Aceita: ```ACHADOS_EXAME...``` (colado) OU ```\nACHADOS_EXAME...``` (quebrado)
    // Tolerância adicionada AI_41 — Sonnet varia a formatação entre chamadas
    const regex = /```\s*ACHADOS_EXAME\s*([\s\S]*?)```/;
    let match = respostaIA.match(regex);

    // ✅ AI_42 Camada A — fallback quando o Sonnet omite o marcador ACHADOS_EXAME
    // Caso observado na Rosiane (biópsia): Sonnet abre o bloco com ``` puro e
    // começa direto com TIPO:/DATA:. A estrutura está correta, só falta o label.
    // Heurística: aceita qualquer bloco cercado por ``` cujas 3 primeiras linhas
    // contenham uma chave reconhecida (TIPO: obrigatório; DATA: ou CONCLUSAO:
    // como âncora adicional para reduzir falso-positivo).
    if (!match || !match[1]) {
      const regexFallback = /```\s*\n([\s\S]*?)\n\s*```/g;
      let candidato;
      while ((candidato = regexFallback.exec(respostaIA)) !== null) {
        const conteudo = candidato[1] || "";
        const primeirasLinhas = conteudo
          .split("\n")
          .slice(0, 3)
          .join("\n")
          .toUpperCase();
        const temTipo = /^\s*TIPO\s*:/m.test(primeirasLinhas);
        const temAncora =
          /^\s*DATA\s*:/m.test(conteudo.toUpperCase()) ||
          /^\s*CONCLUSAO\s*:/m.test(conteudo.toUpperCase());
        if (temTipo && temAncora) {
          console.log(
            "🔧 [AI_42 Camada A] Marcador ACHADOS_EXAME ausente — bloco estruturado recuperado via fallback"
          );
          match = [candidato[0], conteudo] as RegExpMatchArray;
          break;
        }
      }
    }

    if (!match || !match[1]) {
      console.warn("⚠️ Bloco ACHADOS_EXAME não encontrado na resposta da IA");
      return null;
    }

    const blocoAchados = match[1].trim();
    const linhas = blocoAchados.split("\n");

    // Função auxiliar para extrair valor de uma linha
    const extrairValor = (chave: string): string => {
      const linha = linhas.find((l) =>
        l.toUpperCase().startsWith(chave.toUpperCase() + ":")
      );
      if (!linha) return "";
      return linha.substring(linha.indexOf(":") + 1).trim();
    };

    const achados: AchadosExame = {
      tipo: extrairValor("TIPO"),
      data: extrairValor("DATA"),
      lesoesHepaticas: extrairValor("LESOES_HEPATICAS"),
      statusMilao: extrairValor("STATUS_MILAO") as AchadosExame["statusMilao"],
      trombosePortal: extrairValor("TROMBOSE_PORTAL"),
      tipoTrombose: extrairValor(
        "TIPO_TROMBOSE"
      ) as AchadosExame["tipoTrombose"],
      shunts: extrairValor("SHUNTS"),
      ascite: extrairValor("ASCITE") as AchadosExame["ascite"],
      esplenomegalia: extrairValor("ESPLENOMEGALIA"),
      outrosAchados: extrairValor("OUTROS_ACHADOS"),
      conclusao: extrairValor("CONCLUSAO"),
    };

    // ✅ B28.2a (AI_51) — roteamento por tipo de material
    // Se o laudo é de explante hepático, enriquece com os 12 campos
    // de AchadosExplanteExtras. Demais tipos seguem o contrato base.
    const tipoMaterial = detectarTipoMaterial(achados.tipo);
    if (tipoMaterial === "EXPLANTE") {
      Object.assign(achados, extrairCamposExplante(linhas));
    } else if (tipoMaterial === "ENDOSCOPIA") {
      // B301 (AI_137): enriquece com os campos de EDA (varizes, gastropatia, etc.)
      Object.assign(achados, extrairCamposEndoscopia(linhas));
    } else if (tipoMaterial === "ELASTOGRAFIA") {
      // B301 cont. (AI_138): enriquece com rigidez (kPa), METAVIR e CAP.
      Object.assign(achados, extrairCamposElastografia(linhas));
    }

    console.log("✅ Achados extraídos com sucesso:", {
      tipoMaterial,
      achados,
    });
    return achados;
  } catch (error) {
    console.error("❌ Erro ao extrair achados:", error);
    return null;
  }
}

/**
 * Gera um resumo curto para exibição na listagem
 * Máximo ~100 caracteres
 *
 * ✅ B28.2c (AI_51) — roteamento por tipo de material:
 *  - EXPLANTE: resumo histopatológico de explante (pTNM, Milão retrospectivo,
 *    mVI, satelitose, tumor sincrônico, efeito terapêutico).
 *  - BIOPSIA/IMAGEM/ENDOSCOPIA/OUTRO: comportamento original preservado.
 */
export function gerarResumoAchados(achados: AchadosExame): string {
  // ═══ Branch EXPLANTE ═══
  // Em explante, os campos radiológicos (lesoesHepaticas, statusMilao,
  // trombosePortal, shunts) vêm vazios — o Sonnet emite só o bloco
  // histopatológico da Estrutura B. Sem este ramo, o resumo cairia no
  // fallback genérico (primeiros 80 chars da conclusão), perdendo os 12
  // campos ricos de AchadosExplanteExtras.
  if (detectarTipoMaterial(achados.tipo) === "EXPLANTE") {
    return gerarResumoExplante(achados);
  }

  // ═══ Branch ENDOSCOPIA (B301/AI_137) ═══
  // EDA emite campos próprios (varizes eso/gástricas, gastropatia, GAVE, risco) que
  // não existem no schema de imagem. Sem este ramo, o resumo cairia no fallback pobre.
  if (detectarTipoMaterial(achados.tipo) === "ENDOSCOPIA") {
    const resumoEndo = gerarResumoEndoscopia(achados);
    if (resumoEndo) return resumoEndo;
    // sem campos estruturados populados → cai no branch padrão (conclusão)
  }

  // ═══ Branch ELASTOGRAFIA (B301 cont./AI_138) ═══
  // Elastografia emite rigidez (kPa), estágio METAVIR e CAP — não existem no
  // schema de imagem. Sem este ramo o resumo cairia no fallback pobre.
  if (detectarTipoMaterial(achados.tipo) === "ELASTOGRAFIA") {
    const resumoElasto = gerarResumoElastografia(achados);
    if (resumoElasto) return resumoElasto;
    // sem campos estruturados populados → cai no branch padrão (conclusão)
  }

  // ═══ Branch padrão (imagem, biópsia não-explante, endoscopia, outros) ═══
  const partes: string[] = [];

  // Lesões hepáticas (resumido)
  if (
    achados.lesoesHepaticas &&
    achados.lesoesHepaticas !== "Nenhuma identificada"
  ) {
    // Extrair só o essencial: "CHC 3.7cm LI-RADS 5"
    if (achados.lesoesHepaticas.includes("LI-RADS 5")) {
      partes.push("CHC confirmado");
    } else if (achados.lesoesHepaticas.includes("LI-RADS 4")) {
      partes.push("CHC provável");
    } else if (achados.lesoesHepaticas.includes("LI-RADS")) {
      partes.push("Lesão hepática");
    }
  }

  // Status Milão
  if (achados.statusMilao === "DENTRO") {
    partes.push("Milão ✓");
  } else if (achados.statusMilao === "FORA") {
    partes.push("Milão ✗");
  }

  // Trombose portal
  if (achados.trombosePortal && achados.trombosePortal.startsWith("SIM")) {
    const yerdel = achados.trombosePortal.match(/Yerdel\s*(\d)/i);
    if (yerdel) {
      partes.push(`TVP Yerdel ${yerdel[1]}`);
    } else {
      partes.push("TVP");
    }

    if (achados.tipoTrombose === "TUMORAL") {
      partes.push("(TUMORAL!)");
    }
  }

  // Shunts importantes
  if (
    achados.shunts &&
    achados.shunts.toLowerCase().includes("esplenorrenal")
  ) {
    partes.push("Shunt ER");
  }

  // Se não tem nada relevante
  if (partes.length === 0) {
    if (achados.conclusao) {
      // Pegar primeiras palavras da conclusão
      return (
        achados.conclusao.substring(0, 80) +
        (achados.conclusao.length > 80 ? "..." : "")
      );
    }
    return "Sem achados significativos";
  }

  return partes.join(" | ");
}

// ═══════════════════════════════════════════════════════════════════
// 🏥 B28.2c (AI_51) — RESUMO ESPECÍFICO DE EXPLANTE HEPÁTICO
// ═══════════════════════════════════════════════════════════════════
// Prioridade de peças (truncamento agressivo em ~100 chars):
//   1. pTNM completo (preservando prefixo "y")
//   2. Milão retrospectivo sobre viável (✓/✗)
//   3. Tumor sincrônico (flag crítica — histologia distinta)
//   4. Invasão vascular macroscópica (PRESENTE ou PRÉVIA-TRATADA)
//   5. Invasão vascular microscópica (mVI+)
//   6. Satelitose
//   7. Efeito terapêutico quando relevante (necrose baixa = TACE pobre)
//   8. Fallback: "CHC em explante" + primeiros chars da conclusão
// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
// 🔬 B301 (AI_137) — CAMPO ESTRUTURADO DE ENDOSCOPIA: ausência vs presença
// ═══════════════════════════════════════════════════════════════════
// Vocabulário controlado do template (NÃO / F1 / F2 / F3 / GOV1 / IGV1 / LEVE...).
// Um valor conta como AUSENTE quando é vazio ou um token de negação.
const RE_ENDO_AUSENTE =
  /^(n[ãa]o|nenhum[ao]?|ausente|ausentes|sem\b|normal|-|0|f0)/i;

/** true se o campo estruturado de endoscopia indica PRESENÇA (não vazio/negativo). */
export function achadoEndoscopiaPresente(valor?: string): boolean {
  const v = String(valor || "").trim();
  if (!v) return false;
  return !RE_ENDO_AUSENTE.test(v);
}

/**
 * Lê o valor de uma chave `CHAVE: valor` embutida em texto livre — ex.: o bloco
 * ACHADOS_EXAME dentro de `analiseCompleta`. Serve de PONTE (B301) para exames
 * analisados ANTES do parser passar a guardar os campos estruturados: o valor
 * ainda existe no texto integral da análise. "" se a chave não aparecer.
 */
export function lerLinhaEstruturada(texto: string, chave: string): string {
  const re = new RegExp(`(?:^|\\n)\\s*${chave}\\s*:\\s*([^\\n]*)`, "i");
  const m = String(texto || "").match(re);
  return m ? m[1].trim() : "";
}

/**
 * "Cabeça" concisa de um campo: o grau/token essencial, descartando a prosa que o
 * modelo às vezes escreve (o resumo da listagem precisa ser curto). Corta no 1º
 * separador (vírgula, travessão, parêntese, ponto-e-vírgula, barra) e limita o
 * comprimento.
 */
function cabecaCampo(valor: string, maxLen = 22): string {
  let s = String(valor || "")
    .trim()
    .split(/\s*[,;(/]|\s+[-–—]\s+/)[0]
    .trim();
  if (s.length > maxLen) s = s.slice(0, maxLen).trim() + "…";
  return s;
}

/**
 * Resumo curto de ENDOSCOPIA a partir dos campos estruturados (B301).
 * Retorna "" se nenhum campo relevante estiver populado (o chamador então cai no
 * branch padrão, que usa a conclusão).
 */
function gerarResumoEndoscopia(achados: AchadosExame): string {
  const partes: string[] = [];

  if (achadoEndoscopiaPresente(achados.varizesEsofagicas)) {
    partes.push(`VE ${cabecaCampo(achados.varizesEsofagicas || "")}`);
  }
  if (achadoEndoscopiaPresente(achados.varizesGastricas)) {
    partes.push(`VG ${cabecaCampo(achados.varizesGastricas || "")}`);
  }
  if (achadoEndoscopiaPresente(achados.gastropatiaHipertensiva)) {
    partes.push(
      `GHP ${cabecaCampo(achados.gastropatiaHipertensiva || "").toLowerCase()}`
    );
  }
  if (achadoEndoscopiaPresente(achados.gave)) {
    partes.push("GAVE");
  }
  if (achados.classificacaoRisco && achados.classificacaoRisco.trim()) {
    // Extrai só o selo de risco (🔴/🟡/🟢 + ALTO/MODERADO/BAIXO), sem a prosa.
    const m = achados.classificacaoRisco.match(
      /(🔴|🟡|🟢)?\s*(ALTO|MODERADO|BAIXO)/i
    );
    const risco = m
      ? `${m[1] ? m[1] + " " : ""}${m[2].toUpperCase()}`
      : cabecaCampo(achados.classificacaoRisco);
    partes.push(`risco ${risco}`);
  }

  return partes.join(" · ");
}

/**
 * Resumo curto de ELASTOGRAFIA a partir dos campos estruturados (B301 cont.).
 * Ex.: "12,5 kPa · F4 · CAP S2". Retorna "" se nada relevante estiver populado
 * (o chamador então cai no branch padrão, que usa a conclusão).
 */
function gerarResumoElastografia(achados: AchadosExame): string {
  const partes: string[] = [];

  // Rigidez em kPa (só o número + unidade)
  const kpaMatch = String(achados.rigidezKpa || "").match(/(\d+[.,]?\d*)/);
  if (kpaMatch) partes.push(`${kpaMatch[1].replace(".", ",")} kPa`);

  // Estágio METAVIR (F0-F4)
  const metavir = String(achados.fibroseMetavir || "").match(/F[0-4]/i);
  if (metavir) partes.push(metavir[0].toUpperCase());

  // CAP / esteatose (grau S0-S3) — ignora "NÃO REALIZADO"
  const cap = String(achados.capEsteatose || "");
  if (cap && !/n[ãa]o\s*realiz/i.test(cap)) {
    const s = cap.match(/S[0-3]/i);
    if (s) partes.push(`CAP ${s[0].toUpperCase()}`);
  }

  return partes.join(" · ");
}

function gerarResumoExplante(achados: AchadosExame): string {
  const partes: string[] = [];

  // 1. pTNM completo (preserva prefixo "y" quando aplicável)
  if (achados.ptnmCompleto && achados.ptnmCompleto.trim()) {
    partes.push(achados.ptnmCompleto.trim());
  }

  // 2. Milão retrospectivo sobre tumor viável residual
  if (achados.milaoExplanteViavel) {
    const milao = achados.milaoExplanteViavel.toUpperCase();
    if (milao.startsWith("ATENDE") && !milao.startsWith("NÃO ATENDE")) {
      partes.push("Milão ✓");
    } else if (milao.startsWith("NÃO ATENDE")) {
      partes.push("Milão ✗");
    }
  }

  // 3. Tumor sincrônico — flag crítica
  if (
    achados.tumorSincronico &&
    achados.tumorSincronico.trim() &&
    achados.tumorSincronico.toUpperCase() !== "AUSENTE" &&
    achados.tumorSincronico.toUpperCase() !== "NÃO APLICÁVEL"
  ) {
    partes.push("+ Sincrônico");
  }

  // 4. Invasão vascular macroscópica
  if (achados.invasaoVascularMacro === "PRESENTE") {
    partes.push("Macro-VI+");
  } else if (achados.invasaoVascularMacro === "PRÉVIA-TRATADA") {
    partes.push("Macro-VI (tratada)");
  }

  // 5. Invasão vascular microscópica (herdada da base AchadosExame)
  const mvi = (achados as any).invasaoVascularMicro || "";
  if (typeof mvi === "string" && mvi.toUpperCase().startsWith("SIM")) {
    partes.push("mVI+");
  }

  // 6. Satelitose
  if (achados.satelitose === "PRESENTE") {
    partes.push("Satelitose");
  }

  // 7. Efeito terapêutico — sinalizar resposta pobre a TLR prévio
  // Regra: se o campo tem número explícito e é ≤ 50%, sinaliza resposta pobre.
  // "100%", "95%/100%/100%" → não sinaliza (boa resposta).
  if (achados.efeitoTerapeuticoPctNecrose) {
    const ef = achados.efeitoTerapeuticoPctNecrose;
    const numeros = ef.match(/\d+/g);
    if (numeros && numeros.length > 0) {
      const menor = Math.min(...numeros.map((n) => parseInt(n, 10)));
      if (menor <= 50) {
        partes.push(`TLR pobre (${menor}% necrose)`);
      }
    }
  }

  // Fallback: nenhum dos campos-chave populado
  if (partes.length === 0) {
    if (achados.conclusao) {
      return (
        "Explante: " +
        achados.conclusao.substring(0, 70) +
        (achados.conclusao.length > 70 ? "..." : "")
      );
    }
    return "Explante hepático";
  }

  return partes.join(" | ");
}
